import { useEffect, useMemo, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  Mail,
  Search,
  Trash2,
  RefreshCcw,
  ShieldCheck,
  Inbox,
} from "lucide-react";
import { adminApi, auth, formatApiErrorDetail } from "@/lib/admin-auth";

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminDashboard() {
  const [me, setMe] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const navigate = useNavigate();

  const isAuthed = auth.isAuthed();

  useEffect(() => {
    if (!isAuthed) return;
    (async () => {
      try {
        const [meRes, listRes] = await Promise.all([
          adminApi.get("/admin/me"),
          adminApi.get("/admin/inquiries"),
        ]);
        setMe(meRes.data);
        setInquiries(listRes.data || []);
      } catch (err) {
        const msg = formatApiErrorDetail(err?.response?.data?.detail);
        if (err?.response?.status !== 401) toast.error(msg || "Failed to load dashboard.");
      } finally {
        setLoading(false);
      }
    })();
  }, [isAuthed]);

  const filtered = useMemo(() => {
    if (!q.trim()) return inquiries;
    const t = q.toLowerCase();
    return inquiries.filter(
      (i) =>
        (i.full_name || "").toLowerCase().includes(t) ||
        (i.email || "").toLowerCase().includes(t) ||
        (i.organization || "").toLowerCase().includes(t) ||
        (i.service_type || "").toLowerCase().includes(t) ||
        (i.message || "").toLowerCase().includes(t)
    );
  }, [inquiries, q]);

  if (!isAuthed) return <Navigate to="/admin/login" replace />;

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/inquiries");
      setInquiries(data || []);
      toast.success("Refreshed.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to refresh.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    auth.clear();
    toast.success("Signed out.");
    navigate("/admin/login", { replace: true });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Permanently delete this inquiry?")) return;
    setDeleting(id);
    try {
      await adminApi.delete(`/admin/inquiries/${id}`);
      setInquiries((prev) => prev.filter((i) => i.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Inquiry deleted.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed.");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div
      data-testid="admin-dashboard"
      className="min-h-screen bg-[#050505] text-white"
    >
      {/* Top bar */}
      <header className="border-b border-white/5 glass-nav sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="admin-home-link">
            <img
              src="/tbc-logo.jpeg"
              alt="The Bridge Collective"
              className="h-9 w-9 object-contain"
              style={{ filter: "invert(1) brightness(1.05) contrast(1.1)" }}
            />
            <div className="flex flex-col leading-tight">
              <span className="font-display tracking-[0.28em] text-white text-xs font-light">
                THE BRIDGE
              </span>
              <span className="font-display tracking-[0.28em] text-[#1A5C40] text-xs font-light">
                COLLECTIVE
              </span>
            </div>
          </a>

          <div className="flex items-center gap-4">
            {me && (
              <div className="hidden sm:flex items-center gap-2 text-xs text-zinc-400">
                <ShieldCheck size={14} className="text-[#1A5C40]" strokeWidth={1.4} />
                <span data-testid="admin-me-email">{me.email}</span>
              </div>
            )}
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              className="btn-outline px-5 py-2.5"
            >
              <LogOut size={14} strokeWidth={1.5} />
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-8 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
          <div>
            <span className="overline">Admin Console</span>
            <h1 className="font-display text-3xl sm:text-4xl font-light mt-3">
              Inquiries
            </h1>
            <p className="text-sm text-zinc-400 mt-2">
              {inquiries.length} total · sorted by most recent
            </p>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-80">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
                strokeWidth={1.4}
              />
              <input
                data-testid="admin-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, email, message…"
                className="input-luxe pl-9"
              />
            </div>
            <button
              onClick={refresh}
              data-testid="admin-refresh"
              className="btn-outline px-4 py-3"
              title="Refresh"
            >
              <RefreshCcw size={14} strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-zinc-500 py-16 text-center">
            Loading inquiries...
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-white/10 bg-[#0c0c0c] p-14 text-center" data-testid="admin-empty-state">
            <Inbox size={28} className="text-zinc-600 mx-auto" strokeWidth={1.2} />
            <h3 className="font-display mt-5 text-xl text-white">No inquiries yet</h3>
            <p className="text-sm text-zinc-500 mt-2">
              New submissions from the public site will appear here.
            </p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-12 gap-6">
            {/* List */}
            <div className="lg:col-span-7 border border-white/10 bg-[#0c0c0c] divide-y divide-white/5" data-testid="admin-inquiries-list">
              {filtered.map((i) => (
                <button
                  key={i.id}
                  onClick={() => setSelected(i)}
                  data-testid={`admin-inquiry-row-${i.id}`}
                  className={`w-full text-left p-5 hover:bg-[#141414] transition-colors ${
                    selected?.id === i.id ? "bg-[#141414]" : ""
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3">
                        <span className="font-display text-white text-base font-medium truncate">
                          {i.full_name}
                        </span>
                        {i.service_type && (
                          <span className="text-[10px] tracking-[0.22em] uppercase text-[#1A5C40] border border-[#1A5C40]/40 px-2 py-0.5">
                            {i.service_type}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-zinc-500 truncate">
                        {i.email}
                        {i.organization ? ` · ${i.organization}` : ""}
                      </div>
                      <p className="mt-3 text-sm text-zinc-400 line-clamp-2">
                        {i.message}
                      </p>
                    </div>
                    <div className="text-[10px] tracking-[0.18em] uppercase text-zinc-600 whitespace-nowrap">
                      {formatDate(i.created_at)}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Detail */}
            <aside className="lg:col-span-5">
              {selected ? (
                <div className="border border-white/10 bg-[#0c0c0c] p-7 sticky top-28" data-testid="admin-inquiry-detail">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <span className="overline">Inquiry Detail</span>
                      <h3 className="font-display text-xl text-white mt-2">
                        {selected.full_name}
                      </h3>
                    </div>
                    <button
                      onClick={() => handleDelete(selected.id)}
                      disabled={deleting === selected.id}
                      data-testid={`admin-inquiry-delete-${selected.id}`}
                      className="text-zinc-500 hover:text-red-400 transition-colors disabled:opacity-50"
                      title="Delete inquiry"
                    >
                      <Trash2 size={16} strokeWidth={1.4} />
                    </button>
                  </div>

                  <dl className="mt-6 space-y-4 text-sm">
                    <div>
                      <dt className="overline mb-1">Email</dt>
                      <dd>
                        <a
                          href={`mailto:${selected.email}?subject=Re%3A%20Your%20Bridge%20Collective%20Inquiry`}
                          className="text-white hover:text-[#1A5C40] inline-flex items-center gap-2"
                        >
                          {selected.email}
                          <Mail size={13} strokeWidth={1.4} className="text-[#1A5C40]" />
                        </a>
                      </dd>
                    </div>
                    {selected.organization && (
                      <div>
                        <dt className="overline mb-1">Organization</dt>
                        <dd className="text-white">{selected.organization}</dd>
                      </div>
                    )}
                    {selected.service_type && (
                      <div>
                        <dt className="overline mb-1">Service</dt>
                        <dd className="text-white">{selected.service_type}</dd>
                      </div>
                    )}
                    <div>
                      <dt className="overline mb-1">Submitted</dt>
                      <dd className="text-zinc-300">{formatDate(selected.created_at)}</dd>
                    </div>
                    <div>
                      <dt className="overline mb-2">Message</dt>
                      <dd className="text-zinc-300 leading-relaxed whitespace-pre-wrap border-l border-[#1A5C40]/60 pl-4">
                        {selected.message}
                      </dd>
                    </div>
                  </dl>
                </div>
              ) : (
                <div className="border border-white/10 bg-[#0c0c0c] p-7 text-sm text-zinc-500">
                  Select an inquiry to view full details.
                </div>
              )}
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}
